import { authApi } from './authApi';

// Token management configuration
const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiration
const TOKEN_CHECK_INTERVAL = 60 * 1000; // Check every minute
const MAX_TOKEN_LIFETIME = 24 * 60 * 60 * 1000; // 24 hours

interface TokenInfo {
  token: string;
  issuedAt: number;
  expiresAt: number;
}

class TokenManager {
  private refreshTimer: NodeJS.Timeout | null = null;
  private checkTimer: NodeJS.Timeout | null = null;
  private isRefreshing = false;

  constructor() {
    this.startTokenMonitoring();
  }

  // Start monitoring token expiration
  private startTokenMonitoring(): void {
    // Check token status every minute
    this.checkTimer = setInterval(() => {
      this.checkTokenStatus();
    }, TOKEN_CHECK_INTERVAL);
  }

  // Check if token needs refresh
  private async checkTokenStatus(): Promise<void> {
    if (this.isRefreshing) return;

    const token = this.getStoredToken();
    if (!token) return;

    const tokenInfo = this.parseToken(token);
    if (!tokenInfo) return;

    const now = Date.now();
    const timeUntilExpiry = tokenInfo.expiresAt - now;

    // If token expires within threshold, refresh it
    if (timeUntilExpiry <= TOKEN_REFRESH_THRESHOLD && timeUntilExpiry > 0) {
      console.log('[TokenManager] Token expires soon, refreshing...');
      await this.refreshToken();
    }
  }

  // Parse JWT token to get expiration info
  private parseToken(token: string): TokenInfo | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const issuedAt = payload.iat * 1000; // Convert to milliseconds
      const expiresAt = payload.exp * 1000; // Convert to milliseconds
      
      return {
        token,
        issuedAt,
        expiresAt,
      };
    } catch (error) {
      console.warn('[TokenManager] Failed to parse token:', error);
      return null;
    }
  }

  // Get stored token
  private getStoredToken(): string | null {
    return localStorage.getItem('jwt') || sessionStorage.getItem('jwt');
  }

  // Refresh token proactively
  private async refreshToken(): Promise<void> {
    if (this.isRefreshing) return;

    this.isRefreshing = true;

    try {
      console.log('[TokenManager] Proactively refreshing token...');
      const refreshResponse = await authApi.refreshSession();
      
      if (refreshResponse && refreshResponse.user) {
        const newToken = this.getStoredToken();
        if (newToken) {
          console.log('[TokenManager] Token refreshed successfully');
          
          // Schedule next refresh
          const tokenInfo = this.parseToken(newToken);
          if (tokenInfo) {
            const timeUntilNextRefresh = Math.max(
              tokenInfo.expiresAt - Date.now() - TOKEN_REFRESH_THRESHOLD,
              TOKEN_CHECK_INTERVAL
            );
            
            if (this.refreshTimer) {
              clearTimeout(this.refreshTimer);
            }
            
            this.refreshTimer = setTimeout(() => {
              this.refreshToken();
            }, timeUntilNextRefresh);
          }
        }
      }
    } catch (error) {
      console.error('[TokenManager] Failed to refresh token:', error);
      
      // If refresh fails, clear tokens and redirect to login
      this.clearTokens();
      window.location.href = '/login';
    } finally {
      this.isRefreshing = false;
    }
  }

  // Clear stored tokens
  private clearTokens(): void {
    localStorage.removeItem('jwt');
    sessionStorage.removeItem('jwt');
  }

  // Force refresh token (for manual refresh)
  public async forceRefresh(): Promise<boolean> {
    try {
      await this.refreshToken();
      return true;
    } catch (error) {
      console.error('[TokenManager] Force refresh failed:', error);
      return false;
    }
  }

  // Get token expiration info
  public getTokenExpirationInfo(): { isValid: boolean; expiresIn: number; expiresAt: Date | null } {
    const token = this.getStoredToken();
    if (!token) {
      return { isValid: false, expiresIn: 0, expiresAt: null };
    }

    const tokenInfo = this.parseToken(token);
    if (!tokenInfo) {
      return { isValid: false, expiresIn: 0, expiresAt: null };
    }

    const now = Date.now();
    const expiresIn = tokenInfo.expiresAt - now;
    const isValid = expiresIn > 0;

    return {
      isValid,
      expiresIn,
      expiresAt: new Date(tokenInfo.expiresAt),
    };
  }

  // Stop monitoring
  public stop(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
  }
}

// Create singleton instance
export const tokenManager = new TokenManager();

// Export utility functions
export const tokenUtils = {
  // Check if token is valid
  isTokenValid: (): boolean => {
    return tokenManager.getTokenExpirationInfo().isValid;
  },

  // Get time until token expires
  getTimeUntilExpiry: (): number => {
    return tokenManager.getTokenExpirationInfo().expiresIn;
  },

  // Force refresh token
  forceRefresh: (): Promise<boolean> => {
    return tokenManager.forceRefresh();
  },

  // Get token expiration info
  getExpirationInfo: () => {
    return tokenManager.getTokenExpirationInfo();
  },
}; 